import { type AppointmentChainConfiguration } from '@/schemas/admin/appointment-chain-configurations.schema.ts';

export interface MainPractitionerOption {
    practitioner_id: string;
    display_name: string;
}

type ChainStep = AppointmentChainConfiguration['steps'][number];

const isActiveMarkedStep = (step: ChainStep, removedOrders: number[]): boolean =>
    step.is_main_practitioner && !removedOrders.includes(step.order);

export const getActiveMarkedSteps = (config: AppointmentChainConfiguration, removedOrders: number[]): ChainStep[] =>
    config.steps.filter((step) => isActiveMarkedStep(step, removedOrders));

export const hasActiveMarkedSteps = (config: AppointmentChainConfiguration, removedOrders: number[]): boolean =>
    getActiveMarkedSteps(config, removedOrders).length > 0;

export const getEligibleMainPractitioners = (
    config: AppointmentChainConfiguration,
    removedOrders: number[]
): MainPractitionerOption[] => {
    const markedSteps = getActiveMarkedSteps(config, removedOrders);
    if (markedSteps.length === 0) return [];

    const [firstStep, ...restSteps] = markedSteps;
    const firstEligible = firstStep.eligible_practitioners ?? [];

    const commonPractitioners = firstEligible.filter((practitioner) =>
        restSteps.every((step) =>
            (step.eligible_practitioners ?? []).some(
                (candidate) => candidate.practitioner_id === practitioner.practitioner_id
            )
        )
    );

    return commonPractitioners.map((practitioner) => ({
        practitioner_id: practitioner.practitioner_id,
        display_name: practitioner.display_name,
    }));
};

export const getMainPractitionerCalendarIds = (
    config: AppointmentChainConfiguration,
    order: number,
    practitionerId: string
): Set<string> => {
    const step = config.steps.find((s) => s.order === order);
    const practitioner = step?.eligible_practitioners?.find(
        (candidate) => candidate.practitioner_id === practitionerId
    );
    return new Set(practitioner?.calendar_pms_ids);
};
